/**
 * Maps WordPress REST posts (Pods CPT) to `{ id, title, url, summary?, publishedAt?, imageUrl? }`.
 *
 * **CMS fields (pods / meta)** — mirrored in `docs/wp-mu-plugins/iron-lady-podcast-rest-meta.php`:
 * - **Video link**: `youtube_url` (and fallbacks); watch/share URLs → embed URL in the app.
 * - **Episode title**: `episode_title` if set, else WordPress post title (`title.rendered`).
 * - **Summary**: `podcast_summary`, `episode_summary`, or `summary` — plain text. The WP bundle strips
 *   HTML tags. For bold in the app, wrap phrases in double asterisks (see Video Resources rendering).
 * - **Episode image (optional)**: `episode_image_url` or other keys / Featured Image / attachment id — exposed
 *   as `imageUrl` in the bundle and `meta.episode_image_url` for wp/v2. When missing, the UI uses the
 *   YouTube thumbnail derived from `url`.
 */

function stripHtml(s) {
    if (!s || typeof s !== 'string') return '';
    return s.replace(/<[^>]+>/g, '').trim();
}

/** @param {unknown} u */
export function normalizeEpisodeImageUrl(u) {
    if (u == null || u === '') return undefined;
    const s = String(u).trim();
    if (!/^https?:\/\//i.test(s)) return undefined;
    return s;
}

/** Extract 11-char YouTube id from watch, share, short, or embed URLs. */
export function extractYoutubeId(input) {
    if (!input || typeof input !== 'string') return '';
    const u = input.trim();
    const embed = u.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
    if (embed) return embed[1];
    const watch = u.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (watch) return watch[1];
    const short = u.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (short) return short[1];
    const shorts = u.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
    if (shorts) return shorts[1];
    return '';
}

export function youtubeUrlToEmbed(input) {
    const id = extractYoutubeId(input);
    return id ? `https://www.youtube.com/embed/${id}` : '';
}

/** When Pods meta is not exposed in REST, try to find a YouTube URL in post HTML. */
function extractYoutubeUrlFromPostContent(post) {
    const blob = [post.content?.rendered, post.excerpt?.rendered].filter(Boolean).join('\n');
    if (!blob.trim()) return '';
    const hrefMatch = blob.match(
        /href=["'](https?:\/\/[^"']*(?:youtube\.com\/|youtu\.be\/)[^"']*)["']/i
    );
    const bareUrl = blob.match(
        /https?:\/\/(?:www\.)?(?:youtu\.be\/[a-zA-Z0-9_-]{11}(?:[?&][^\s<'"]*)?|youtube\.com\/watch\?[^\s<'"]+)/i
    );
    const candidate = hrefMatch
        ? hrefMatch[1]
        : bareUrl
          ? bareUrl[0]
          : blob.replace(/<[^>]*>/g, ' ');
    const id = extractYoutubeId(candidate);
    return id ? `https://www.youtube.com/embed/${id}` : '';
}

/**
 * @param {object} post - WP REST post object
 * @returns {{ id: number, title: string, url: string, summary?: string, publishedAt?: string, imageUrl?: string } | null}
 */
export function mapWordPressPostToVideo(post) {
    if (!post || typeof post !== 'object') return null;
    const meta = post.meta || {};
    const rawUrl =
        meta.youtube_url ||
        meta.youtube_link ||
        meta.youtube ||
        meta.pods_youtube_url ||
        meta.podcast_youtube_url ||
        '';
    const fromContent = extractYoutubeUrlFromPostContent(post);
    const combined = rawUrl || fromContent;
    let url =
        youtubeUrlToEmbed(combined) ||
        (typeof combined === 'string' && combined.includes('youtube.com/embed')
            ? combined.split('?')[0]
            : '');
    const title =
        stripHtml(meta.episode_title) ||
        stripHtml(post.title?.rendered) ||
        'Untitled';
    if (!url) return null;
    const rawSummary = meta.podcast_summary || meta.episode_summary || meta.summary || '';
    const fromExcerpt = stripHtml(post.excerpt?.rendered || '');
    const summaryText = rawSummary ? stripHtml(String(rawSummary)) : fromExcerpt;
    const summary = summaryText && summaryText.trim() ? summaryText.trim() : undefined;
    const publishedAt =
        typeof post.date === 'string' && post.date !== ''
            ? post.date
            : typeof post.date_gmt === 'string' && post.date_gmt !== ''
              ? post.date_gmt
              : undefined;
    const imageUrl = normalizeEpisodeImageUrl(
        post.imageUrl ?? meta.episode_image_url ?? meta.podcast_image_url ?? meta.hero_image_url ?? meta.podcast_image,
    );

    const out = {
        id: post.id,
        title,
        url,
        ...(summary ? { summary } : {}),
        ...(publishedAt ? { publishedAt } : {}),
        ...(imageUrl ? { imageUrl } : {}),
    };
    return out;
}

function cacheBustParam() {
    return new URLSearchParams({ cb: String(Date.now()) }).toString();
}

function withCacheBust(url) {
    if (!url || typeof url !== 'string') return url;
    return url.includes('?') ? `${url}&${cacheBustParam()}` : `${url}?${cacheBustParam()}`;
}

function isValidCmsVideoItem(x) {
    if (!x || typeof x !== 'object') return false;
    const { id, title, url } = x;
    const pid = typeof id === 'number' ? id : Number(id);
    return (
        Number.isFinite(pid) &&
        typeof title === 'string' &&
        title.length > 0 &&
        typeof url === 'string' &&
        (url.includes('youtube.com/embed') || url.includes('youtu.be/'))
    );
}

/**
 * Fetches from /il/v1/video-podcasts (server reads meta + Pods), then falls back to wp/v2.
 * @returns {Promise<{
 *   list: Array<{ id: number, title: string, url: string }>,
 *   bundleStatus: number | null,
 *   v2PostCount: number,
 *   v2MappedCount: number,
 * }>}
 */
export async function loadPodcastEpisodes() {
    const defaultBundle = 'https://cms.iamironlady.com/wp-json/il/v1/video-podcasts';
    const defaultV2 = 'https://cms.iamironlady.com/wp-json/wp/v2/podcast?per_page=100&orderby=date&order=desc';
    const bundleBase = process.env.REACT_APP_PODCAST_BUNDLE_URL || defaultBundle;
    const v2Base = process.env.REACT_APP_PODCAST_REST_URL || defaultV2;
    const fetchOpts = { credentials: 'omit', cache: 'no-store' };
    const out = {
        list: [],
        bundleStatus: null,
        v2PostCount: 0,
        v2MappedCount: 0,
    };

    try {
        const bUrl = withCacheBust(bundleBase);
        const bRes = await fetch(bUrl, fetchOpts);
        out.bundleStatus = bRes.status;
        if (bRes.ok) {
            const data = await bRes.json();
            if (Array.isArray(data) && data.length) {
                const list = data.filter(isValidCmsVideoItem);
                if (list.length) {
                    out.list = list;
                    return out;
                }
            }
        }
    } catch (e) {
        out.bundleStatus = 0;
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn('[Podcasts] Bundle endpoint not available, using wp/v2 list.', e);
        }
    }

    const v2Res = await fetch(withCacheBust(v2Base), fetchOpts);
    if (!v2Res.ok) {
        throw new Error(`Podcast REST ${v2Res.status}: ${v2Res.statusText}`);
    }
    const json = await v2Res.json();
    const posts = Array.isArray(json) ? json : [];
    out.v2PostCount = posts.length;
    const mapped = mapWordPressPodcastResponse(json);
    out.v2MappedCount = mapped.length;
    out.list = mapped;
    return out;
}

/**
 * Tries the Iron Lady bundle endpoint (reads post meta + Pods on the server), then wp/v2.
 * @returns {Promise<Array<{ id: number, title: string, url: string }>>}
 */
export async function fetchAllPodcasts() {
    const { list } = await loadPodcastEpisodes();
    return list;
}

async function fetchPodcastsFromWordPressV2(restUrl) {
    const res = await fetch(restUrl, {
        credentials: 'omit',
        cache: 'no-store',
    });
    if (!res.ok) {
        throw new Error(`Podcast REST ${res.status}: ${res.statusText}`);
    }
    const json = await res.json();
    return mapWordPressPodcastResponse(json);
}

/**
 * @param {unknown} json - WP REST response (array of posts or wrapped)
 * @returns {Array<{ id: number, title: string, url: string }>}
 */
export function mapWordPressPodcastResponse(json) {
    const posts = Array.isArray(json) ? json : json?.data;
    if (!Array.isArray(posts)) return [];
    const mapped = posts.map(mapWordPressPostToVideo).filter(Boolean);
    if (
        process.env.NODE_ENV === 'development' &&
        posts.length > 0 &&
        mapped.length === 0
    ) {
        // Typical cause: Pods meta keys not registered for REST — REST JSON has no meta.youtube_url
        // eslint-disable-next-line no-console
        console.warn(
            '[Video Resources / Podcasts] WordPress returned podcast posts, but none could be used. ' +
                'The REST response must include meta.youtube_url (or register the field with show_in_rest). ' +
                'In Pods: edit the youtube_url field → REST API → expose to REST. Republish the post after saving the pod.'
        );
    }
    return mapped;
}

/**
 * @deprecated Prefer {@link fetchAllPodcasts} — it uses the il/v1 bundle and falls back to wp/v2.
 * @param {string} restUrl - Full REST URL for wp/v2/podcast only
 */
export async function fetchPodcastsFromWordPress(restUrl) {
    return fetchPodcastsFromWordPressV2(restUrl);
}
